/**
 * Device key storage using IndexedDB
 *
 * Stores device key pair securely in browser storage.
 * Private key is non-extractable for security.
 */

import type { DeviceKeyPair } from '@yada/types';
import { createLogger } from '@yada/logger';

const logger = createLogger('deviceStorage');

const DB_NAME = 'yada_device_keys';
const DB_VERSION = 1;
const STORE_NAME = 'device_keys';

/**
 * Tracks whether the current session's device key was regenerated
 * (IndexedDB was cleared but deviceId survived in localStorage).
 * When true, uploading the new public key would destroy seed access.
 */
let _keyWasRegenerated = false;

/**
 * Returns true if the device key was regenerated in this session
 * because IndexedDB was cleared. Callers should NOT upload the
 * regenerated public key if seeds already exist on the server.
 */
export function wasDeviceKeyRegenerated(): boolean {
  return _keyWasRegenerated;
}

interface StoredDeviceKey {
  deviceId: string;
  publicKey: CryptoKey | JsonWebKey;
  privateKey: CryptoKey | JsonWebKey;
  algorithm: string;
  createdAt: string;
}

/**
 * Initialize IndexedDB
 */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'deviceId' });
      }
    };
  });
}

/**
 * Store device key pair in IndexedDB.
 * Stores CryptoKey objects directly (supported via structured clone) so
 * private keys remain non-extractable.
 */
export async function storeDeviceKey(keyPair: DeviceKeyPair): Promise<void> {
  const db = await openDB();

  const stored: StoredDeviceKey = {
    deviceId: keyPair.deviceId,
    publicKey: keyPair.publicKey,
    privateKey: keyPair.privateKey,
    algorithm: keyPair.algorithm,
    createdAt: keyPair.createdAt,
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(stored);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

/**
 * Get device key pair from IndexedDB.
 * Supports both direct CryptoKey objects and legacy JWK records.
 */
export async function getDeviceKey(deviceId: string): Promise<DeviceKeyPair | null> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(deviceId);

    request.onerror = () => reject(request.error);
    request.onsuccess = async () => {
      const stored = request.result as StoredDeviceKey | undefined;
      if (!stored) {
        resolve(null);
        return;
      }

      // If stored directly as CryptoKey, return immediately
      if (stored.publicKey instanceof CryptoKey && stored.privateKey instanceof CryptoKey) {
        resolve({
          deviceId: stored.deviceId,
          publicKey: stored.publicKey,
          privateKey: stored.privateKey,
          algorithm: stored.algorithm as any,
          createdAt: stored.createdAt,
        });
        return;
      }

      // Legacy fallback: import keys from JWK if stored as plain object
      const isECC = stored.algorithm === 'X25519';
      const algorithmParams: any = isECC 
        ? { name: 'X25519' } 
        : { name: 'RSA-OAEP', hash: 'SHA-256' };

      const publicKey = stored.publicKey instanceof CryptoKey
        ? stored.publicKey
        : await crypto.subtle.importKey(
            'jwk',
            stored.publicKey as JsonWebKey,
            algorithmParams,
            true,
            isECC ? [] : ['encrypt']
          );

      const privateKey = stored.privateKey instanceof CryptoKey
        ? stored.privateKey
        : await crypto.subtle.importKey(
            'jwk',
            stored.privateKey as JsonWebKey,
            algorithmParams,
            false, // non-extractable on legacy import
            isECC ? ['deriveKey', 'deriveBits'] : ['decrypt']
          );

      resolve({
        deviceId: stored.deviceId,
        publicKey,
        privateKey,
        algorithm: stored.algorithm as any,
        createdAt: stored.createdAt,
      });
    };
  });
}

/**
 * Get current device ID from localStorage
 */
export function getCurrentDeviceId(): string | null {
  return localStorage.getItem('yada:deviceId');
}

/**
 * Set current device ID in localStorage
 */
export function setCurrentDeviceId(deviceId: string): void {
  localStorage.setItem('yada:deviceId', deviceId);
}

/**
 * Recover deviceId from IndexedDB when localStorage has lost it.
 * Enumerates stored keys and returns the first deviceId found.
 */
export async function recoverDeviceIdFromIndexedDB(): Promise<string | null> {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.openCursor();

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const stored = cursor.value as StoredDeviceKey;
          resolve(stored.deviceId);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

/**
 * Get or create device key pair
 *
 * IMPORTANT: If a deviceId exists in localStorage, we MUST use it when creating
 * a new keypair. This is because the deviceId is already embedded in the JWT
 * after login, and the auth server validates that uploaded keys match the JWT's
 * deviceId. Generating a new deviceId would cause a mismatch.
 *
 * Recovery: If localStorage lost the deviceId but IndexedDB still has the key,
 * we recover the deviceId from IndexedDB to maintain device continuity.
 */
export async function getOrCreateDeviceKey(): Promise<DeviceKeyPair> {
  let existingDeviceId = getCurrentDeviceId();

  // If localStorage lost the deviceId, try recovering from IndexedDB
  if (!existingDeviceId) {
    const recoveredId = await recoverDeviceIdFromIndexedDB();
    if (recoveredId) {
      logger.log('Recovered deviceId from IndexedDB:', recoveredId);
      setCurrentDeviceId(recoveredId);
      existingDeviceId = recoveredId;
    }
  }

  // If we have a device ID, try to load the key
  if (existingDeviceId) {
    const existing = await getDeviceKey(existingDeviceId);
    if (existing) {
      logger.log('Loaded existing device key for:', existingDeviceId);
      _keyWasRegenerated = false;
      return existing;
    }

    // Key doesn't exist in IndexedDB, but we MUST use the existing deviceId
    // because it's already in our JWT from login.
    // WARNING: This new key will NOT match seeds wrapped with the old key.
    logger.warn('Device key missing from IndexedDB for deviceId:', existingDeviceId);
    logger.warn('Regenerating key pair — existing seeds will NOT be decryptable with this key');
    const { generateDeviceKey } = await import('./keyManagement');
    const keyPair = await generateDeviceKey(existingDeviceId);

    // Store in IndexedDB (deviceId is already in localStorage)
    await storeDeviceKey(keyPair);
    _keyWasRegenerated = true;

    return keyPair;
  }

  // No device ID exists anywhere - generate new device ID and key pair
  // This only happens on first-ever login on this device
  const { generateDeviceKey } = await import('./keyManagement');
  const newDeviceId = crypto.randomUUID();
  const keyPair = await generateDeviceKey(newDeviceId);

  // Store in IndexedDB and localStorage
  await storeDeviceKey(keyPair);
  setCurrentDeviceId(newDeviceId);
  _keyWasRegenerated = false;

  return keyPair;
}
